import type { PrismTool } from '../engine/prism.js';
import { create } from 'zustand';

import { getView } from '../engine/prism.js';
import { clearOverlay, setOverlay } from '../engine/host.js';
import { docIO, SAVE_DELAY_MS } from '../lib/doc-io.js';
import {
  addPart,
  movePart,
  newModel,
  parseModel,
  partPathOf,
  resolvePartPath,
  serializeModel,
  type IndexPath,
  type ModelDoc,
  type ModelPart,
} from '../lib/model-doc.js';
import { deleteParts, duplicateParts, effectiveSelection, selectionRoots } from '../lib/model-tree.js';
import { logEditor } from './console.js';
import {
  DEFAULT_MESH_OPTIONS,
  INACTIVE_MESH_EDIT,
  parseMeshOptions,
  type MeshEditState,
  type MeshLabel,
  type MeshMode,
  type MeshOptions,
} from './mesh-edit.js';
import { useWorkspace } from './workspace.js';
import { DEFAULT_SCULPT_BRUSH, type SculptBrush, type SculptState } from './sculpt.js';
import { followActive } from './follow.js';

export type { MeshEditState, MeshMode, MeshOptions } from './mesh-edit.js';

/**
 * The open model document (`*.model.json`).
 *
 * The Model tab edits the document directly; the engine view re-reads it
 * through the host overlay (`view.fileChanged`) after each edit, and the file
 * is saved after a short debounce. Selection is kept as a prism part path
 * (`body/arm#2`) so it survives reloads and reads well in AI requests.
 */

const HISTORY_LIMIT = 100;

export type SaveState = 'saved' | 'pending' | 'saving' | 'error';
export type Shading = 'smooth' | 'flat';

export interface ModelState {
  path: string | null;
  doc: ModelDoc | null;
  loading: boolean;
  error: string | null;
  /** Selected part as a prism part path. */
  selected: string | null;
  /**
   * Every selected part (Parts panel multi-selection), the active `selected`
   * included; read it through `effectiveSelection(selected, multi)`.
   */
  multi: string[];
  collapsed: string[];
  wireframe: boolean;
  shading: Shading;
  /** Vertex/edge/face editing of a `mesh` part in the viewport. */
  meshEdit: MeshEditState;
  /** Modeling viewport options: proportional, snapping, mirror, overlays, shading. */
  meshOptions: MeshOptions;
  /** Measurement overlay labels (CSS pixels in the viewport). */
  meshLabels: MeshLabel[];
  /** Brush sculpting of a `sculpt` part in the viewport (engine-owned). */
  sculpt: SculptState;
  /** Show models that change on disk (AI agents) while nothing unsaved is open. */
  followAgent: boolean;
  /** Viewport gizmo: transforms the selected part. */
  tool: PrismTool;
  space: 'world' | 'local';
  snap: boolean;
  grid: boolean;
  saveState: SaveState;
  past: ModelDoc[];
  future: ModelDoc[];

  open: (path: string) => Promise<void>;
  /** Create a new model file and open it. */
  create: (path: string, name: string) => Promise<void>;
  close: () => void;
  /** Replace the whole document (inspector edits). */
  update: (doc: ModelDoc, select?: IndexPath | null, multi?: string[]) => void;
  select: (partPath: string | null) => void;
  /** Select several parts; `active` (default: the last) is the one the inspector and gizmo use. */
  selectMany: (partPaths: string[], active?: string | null) => void;
  selectedIndex: () => number[] | null;
  addPart: (part: ModelPart, asChild?: boolean) => void;
  deleteSelected: () => void;
  duplicateSelected: () => void;
  moveSelected: (delta: -1 | 1) => void;
  toggleCollapsed: (partPath: string) => void;
  setWireframe: (on: boolean) => void;
  setTool: (tool: PrismTool) => void;
  convertToMesh: () => void;
  setMeshEdit: (on: boolean) => void;
  setMeshFaces: (faces: boolean) => void;
  setMeshMode: (mode: MeshMode) => void;
  /** Runs a mesh operation (`prism_model` MeshOp name + parameters, or an amount). */
  meshOp: (name: MeshOpName | string, params?: number | Record<string, unknown>) => void;
  /** Starts an interactive tool in the viewport (loopCut, knife, bevel, ...). */
  meshTool: (tool: string) => void;
  /** Re-runs the last mesh operation with changed parameters (one undo step). */
  meshRedoLast: (params: Record<string, unknown>) => void;
  setMeshOptions: (patch: Partial<MeshOptions>) => void;
  meshFromEngine: (state: MeshEditState, options?: unknown) => void;
  meshLabelsFromEngine: (labels: MeshLabel[]) => void;
  setSculpt: (on: boolean) => void;
  setSculptBrush: (brush: Partial<SculptBrush>) => void;
  sculptFromEngine: (active: boolean, path: string | null, brush: unknown) => void;
  setFollowAgent: (on: boolean) => void;
  /** A model file other than the open one changed on disk. */
  followExternal: (path: string) => Promise<void>;
  setSpace: (space: 'world' | 'local') => void;
  setSnap: (snap: boolean) => void;
  /** Gizmo mode/space changed from the viewport's own shortcuts. */
  gizmoFromEngine: (mode: string, space: string) => void;
  setShading: (shading: Shading) => void;
  setGrid: (on: boolean) => void;
  frameSelected: () => void;
  undo: () => void;
  redo: () => void;
  /** Document from the engine; `replace` adjusts the latest step instead of adding one. */
  /** `reload`: the engine's answer to a reload the editor asked for (engines that mark it). */
  fromEngine: (path: string, doc: unknown, replace?: boolean, reload?: boolean) => void;
  externalChange: (path: string) => Promise<void>;
  flush: () => Promise<void>;
}

// Sculpt types live in state/sculpt.ts (brush palette, falloff curves, sculpt modelOps).
export { DEFAULT_SCULPT_BRUSH, type SculptBrush, type SculptBrushName, type SculptState } from './sculpt.js';

const FOLLOW_KEY = 'prism.model.followAgent';
const MESH_OPTIONS_KEY = 'prism.model.meshOptions';
function readMeshOptions(): MeshOptions {
  try {
    return parseMeshOptions(JSON.parse(localStorage.getItem(MESH_OPTIONS_KEY) ?? 'null'));
  } catch {
    return DEFAULT_MESH_OPTIONS;
  }
}
function readFollow(): boolean {
  try {
    return localStorage.getItem(FOLLOW_KEY) !== 'false';
  } catch {
    return true;
  }
}

export type MeshOpName = 'extrude' | 'inset' | 'subdivide' | 'delete' | 'merge' | 'flip' | 'crease' | 'selectAll';

/** Sends a model command to the viewport; failures go to the console. */
function modelCommand(command: Record<string, unknown>): void {
  void getView('model')
    ?.modelCommand(command)
    .then((outcome) => {
      if (!outcome.ok && outcome.error) logEditor('warn', outcome.error);
    });
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
const lastDisk = new Map<string, string>();
/**
 * Texts this editor saved to each model lately. The file watcher reports
 * every save, so a quick edit can be followed by the echo of an older save:
 * that is not someone else's change and must not undo the newer edit.
 */
const ownWrites = new Map<string, string[]>();
function rememberWrite(path: string, text: string): void {
  const list = ownWrites.get(path) ?? [];
  list.push(text);
  if (list.length > 8) list.shift();
  ownWrites.set(path, list);
}

/**
 * Reloads of the open model the engine still owes a `modelChanged` answer
 * for. Every edit hands the engine the new document, and the engine answers
 * with the document it rebuilt; while edits come quicker than that round
 * trip the answer is older than the editor's document and must not replace
 * it. Engines that mark their answers (`reload`) make the count unnecessary.
 */
let pendingEchoes = 0;
let pendingSince = 0;
let lastSent = '';
const ECHO_WINDOW_MS = 5000;

function projectId(): number | null {
  return useWorkspace.getState().projectId;
}

export const useModel = create<ModelState>((set, get) => {
  function commit(
    doc: ModelDoc,
    options: { history?: boolean; save?: boolean; select?: IndexPath | null; multi?: string[]; fromEngine?: boolean } = {},
  ): void {
    const { history = true, save = true } = options;
    const state = get();
    const prev = state.doc;
    let selected = state.selected;
    if (options.select !== undefined) {
      selected = options.select && options.select.length ? partPathOf(doc, options.select) : null;
    } else if (selected && !resolvePartPath(doc, selected)) {
      selected = null;
    }
    const multi = (options.multi ?? (options.select !== undefined ? (selected ? [selected] : []) : state.multi)).filter((p) =>
      resolvePartPath(doc, p),
    );
    set({
      doc,
      selected,
      multi,
      past: history && prev ? [...state.past, prev].slice(-HISTORY_LIMIT) : state.past,
      future: history ? [] : state.future,
    });
    if (state.path) {
      const text = serializeModel(doc);
      setOverlay(state.path, text);
      // The engine already has a document it reported; an unchanged one gets no answer.
      if (!options.fromEngine && text !== lastSent) {
        pendingEchoes += 1;
        pendingSince = Date.now();
      }
      lastSent = text;
      void getView('model')?.fileChanged(state.path);
    }
    if (save) scheduleSave();
  }

  function scheduleSave(): void {
    if (saveTimer) clearTimeout(saveTimer);
    set({ saveState: 'pending' });
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void get().flush();
    }, SAVE_DELAY_MS);
  }

  return {
    path: null,
    doc: null,
    loading: false,
    error: null,
    selected: null,
    multi: [],
    collapsed: [],
    wireframe: false,
    shading: 'smooth',
    tool: 'move',
    space: 'world',
    snap: false,
    meshEdit: INACTIVE_MESH_EDIT,
    meshOptions: readMeshOptions(),
    meshLabels: [],
    sculpt: { active: false, path: null, brush: DEFAULT_SCULPT_BRUSH },
    followAgent: readFollow(),
    grid: true,
    saveState: 'saved',
    past: [],
    future: [],

    open: async (path) => {
      const pid = projectId();
      if (!pid) return;
      await get().flush();
      set({ path, loading: true, error: null });
      try {
        const text = await docIO().read(pid, path);
        if (get().path !== path) return;
        const doc = parseModel(text);
        lastDisk.set(path, text);
        setOverlay(path, text);
        set({ doc, loading: false, selected: null, past: [], future: [], collapsed: [], saveState: 'saved' });
        void getView('model')?.openModel(path);
      } catch (error) {
        if (get().path !== path) return;
        set({ doc: null, loading: false, error: `${path}: ${error instanceof Error ? error.message : String(error)}` });
      }
    },

    create: async (path, name) => {
      const pid = projectId();
      if (!pid) return;
      await get().flush();
      const doc = newModel(name);
      const text = serializeModel(doc);
      try {
        await docIO().write(pid, path, text);
      } catch (error) {
        logEditor('error', `Could not create ${path}: ${error instanceof Error ? error.message : error}`);
        return;
      }
      lastDisk.set(path, text);
      setOverlay(path, text);
      set({ path, doc, loading: false, error: null, selected: 'body', past: [], future: [], saveState: 'saved' });
      void getView('model')?.openModel(path);
    },

    close: () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path } = get();
      if (path) clearOverlay(path);
      set({ path: null, doc: null, error: null, selected: null, past: [], future: [], saveState: 'saved' });
    },

    update: (doc, select, multi) => commit(doc, { ...(select === undefined ? {} : { select }), ...(multi ? { multi } : {}) }),

    select: (selected) => {
      set({ selected, multi: selected ? [selected] : [] });
      getView('model')?.setOption('selection', selected ?? '');
    },

    selectMany: (partPaths, active) => {
      const selected = active === undefined ? (partPaths.at(-1) ?? null) : active;
      const multi = selected && !partPaths.includes(selected) ? [...partPaths, selected] : partPaths;
      set({ selected, multi });
      getView('model')?.setOption('selection', selected ?? '');
    },

    selectedIndex: () => {
      const { doc, selected } = get();
      return doc && selected ? resolvePartPath(doc, selected) : null;
    },

    addPart: (part, asChild = false) => {
      const { doc } = get();
      if (!doc) return;
      const parent = asChild ? get().selectedIndex() : null;
      const result = addPart(doc, parent, part);
      if (!result.path.length) return;
      commit(result.doc, { select: result.path });
    },

    deleteSelected: () => {
      const { doc, selected, multi } = get();
      const parts = effectiveSelection(selected, multi);
      if (!doc || !parts.length) return;
      commit(deleteParts(doc, parts), { select: null });
    },

    duplicateSelected: () => {
      const { doc, selected, multi } = get();
      const parts = effectiveSelection(selected, multi);
      if (!doc || !parts.length) return;
      const result = duplicateParts(doc, parts);
      const roots = selectionRoots(doc, parts);
      const active = result.paths[selected ? roots.indexOf(selected) : -1] ?? result.paths.at(-1) ?? null;
      commit(result.doc, { select: active, multi: result.paths.map((p) => partPathOf(result.doc, p)) });
    },

    moveSelected: (delta) => {
      const { doc } = get();
      const index = get().selectedIndex();
      if (!doc || !index) return;
      const result = movePart(doc, index, delta);
      if (result.doc !== doc) commit(result.doc, { select: result.path });
    },

    toggleCollapsed: (partPath) =>
      set((state) => ({
        collapsed: state.collapsed.includes(partPath)
          ? state.collapsed.filter((c) => c !== partPath)
          : [...state.collapsed, partPath],
      })),

    setTool: (tool) => {
      set({ tool });
      getView('model')?.setTool(tool);
    },
    convertToMesh: () => {
      const path = get().selected;
      if (path) modelCommand({ op: 'convertToMesh', path });
    },
    setMeshEdit: (on) => modelCommand({ op: 'editMesh', on }),
    setMeshFaces: (faces) => modelCommand({ op: 'meshSelectMode', faces }),
    setMeshMode: (mode) => modelCommand({ op: 'meshSelectMode', mode }),
    meshOp: (name, params) =>
      modelCommand({ op: 'meshOp', name, ...(typeof params === 'number' ? { amount: params } : (params ?? {})) }),
    meshTool: (tool) => modelCommand({ op: 'meshOp', name: 'tool', tool }),
    meshRedoLast: (params) => modelCommand({ op: 'meshOp', name: 'redoLast', ...params }),
    setMeshOptions: (patch) => {
      const meshOptions = { ...get().meshOptions, ...patch };
      set({ meshOptions });
      try {
        localStorage.setItem(MESH_OPTIONS_KEY, JSON.stringify(meshOptions));
      } catch {
        // Storage unavailable: options last for this session.
      }
      getView('model')?.setOption('meshEdit', JSON.stringify(patch));
    },
    meshFromEngine: (meshEdit, options) =>
      set(options === undefined ? { meshEdit } : { meshEdit, meshOptions: parseMeshOptions(options, get().meshOptions) }),
    meshLabelsFromEngine: (meshLabels) => set({ meshLabels }),
    setSculpt: (on) => {
      if (on) getView('model')?.setOption('sculptBrush', JSON.stringify(get().sculpt.brush));
      modelCommand({ op: 'editSculpt', on });
    },
    setSculptBrush: (patch) => {
      const brush = { ...get().sculpt.brush, ...patch };
      set({ sculpt: { ...get().sculpt, brush } });
      getView('model')?.setOption('sculptBrush', JSON.stringify(brush));
    },
    sculptFromEngine: (active, path, raw) => {
      const brush =
        raw && typeof raw === 'object' ? { ...DEFAULT_SCULPT_BRUSH, ...(raw as Partial<SculptBrush>) } : get().sculpt.brush;
      set({ sculpt: { active, path, brush } });
    },
    setFollowAgent: (followAgent) => {
      set({ followAgent });
      try {
        localStorage.setItem(FOLLOW_KEY, String(followAgent));
      } catch {
        // Storage unavailable: the choice lasts for this session.
      }
    },
    followExternal: async (path) => {
      const { path: current, saveState, loading, sculpt } = get();
      const name = path.split('/').pop() ?? '';
      if (!followActive() || loading || sculpt.active || !name.endsWith('.model.json') || name.startsWith('.')) return;
      if (current && saveState !== 'saved') return;
      const pid = projectId();
      if (!pid) return;
      try {
        await docIO().read(pid, path);
      } catch {
        return; // deleted or unreadable
      }
      if (get().path === path) return;
      logEditor('info', `Showing ${path}: it changed on disk (Follow assistant is on)`);
      await get().open(path);
    },
    setSpace: (space) => {
      set({ space });
      getView('model')?.setOption('space', space);
    },
    setSnap: (snap) => {
      set({ snap });
      getView('model')?.setOption('snap', String(snap));
    },
    gizmoFromEngine: (mode, space) => {
      const tool: PrismTool =
        mode === 'translate' ? 'move' : mode === 'rotate' ? 'rotate' : mode === 'scale' ? 'scale' : 'select';
      set({ tool, space: space === 'local' ? 'local' : 'world' });
    },
    setWireframe: (wireframe) => {
      set({ wireframe });
      getView('model')?.setOption('wireframe', String(wireframe));
    },
    setShading: (shading) => {
      set({ shading });
      getView('model')?.setOption('shading', shading);
    },
    setGrid: (grid) => {
      set({ grid });
      getView('model')?.setOption('grid', String(grid));
    },
    frameSelected: () => {
      getView('model')?.setOption('frame', get().selected ?? '');
    },

    undo: () => {
      const { past, doc, future } = get();
      const prev = past.at(-1);
      if (!prev || !doc) return;
      set({ past: past.slice(0, -1), future: [doc, ...future].slice(0, HISTORY_LIMIT) });
      commit(prev, { history: false });
    },

    redo: () => {
      const { past, doc, future } = get();
      const next = future[0];
      if (!next || !doc) return;
      set({ past: [...past, doc].slice(-HISTORY_LIMIT), future: future.slice(1) });
      commit(next, { history: false });
    },

    fromEngine: (path, raw, replace, reload) => {
      if (path !== get().path || !raw || typeof raw !== 'object') return;
      // The engine answering a reload this editor asked for (its document is
      // the editor's own, possibly older one): not a change to adopt.
      if (pendingEchoes > 0 && Date.now() - pendingSince >= ECHO_WINDOW_MS) pendingEchoes = 0; // answers that never came
      const echo = reload ?? pendingEchoes > 0;
      if (echo && pendingEchoes > 0) pendingEchoes -= 1;
      if (echo) return;
      try {
        const doc = parseModel(JSON.stringify(raw));
        // The engine echoes documents it reloaded (after undo / redo): not a new step.
        const current = get().doc;
        if (current && serializeModel(current) === serializeModel(doc)) return;
        commit(doc, { ...(replace && get().past.length ? { history: false } : {}), fromEngine: true });
      } catch {
        // Ignore malformed engine payloads.
      }
    },

    externalChange: async (path) => {
      const pid = projectId();
      if (!pid || path !== get().path) return;
      let text: string;
      try {
        text = await docIO().read(pid, path);
      } catch {
        return;
      }
      if (get().path !== path) return;
      const doc = get().doc;
      if (text === lastDisk.get(path) || ownWrites.get(path)?.includes(text) || (doc && text === serializeModel(doc))) return;
      lastDisk.set(path, text);
      let parsed: ModelDoc;
      try {
        parsed = parseModel(text);
      } catch (error) {
        set({ error: `${path}: ${error instanceof Error ? error.message : String(error)}` });
        return;
      }
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      set({ error: null, saveState: 'saved' });
      commit(parsed, { save: false });
    },

    flush: async () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path, doc, saveState } = get();
      const pid = projectId();
      if (!path || !doc || !pid || saveState === 'saved') return;
      const text = serializeModel(doc);
      if (text === lastDisk.get(path)) {
        set({ saveState: 'saved' });
        return;
      }
      set({ saveState: 'saving' });
      try {
        lastDisk.set(path, text);
        rememberWrite(path, text);
        await docIO().write(pid, path, text);
        if (get().path === path && get().saveState === 'saving') set({ saveState: 'saved' });
      } catch (error) {
        lastDisk.delete(path);
        set({ saveState: 'error' });
        logEditor('error', `Could not save ${path}: ${error instanceof Error ? error.message : error}`);
      }
    },
  };
});
